/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import mbpLogger from '@module-logger/mbp';
import { endConversation } from '../../../../apis/webchat-apis/v2/endConversation';
import { storeAttributionDataFromChatWidget } from '../../../helpers/attribution/attributionHelpers';
import { USE_CASES, MAX_INVALID_PROMO_CODES_ATTEMPTS, SUGGEST_PROMO_CODE_USE_CASE } from '../constants/useCases';
import { parseBannerCodes } from '../utils/commonUtils';
import { playAudio } from '../utils/audioHelper';
import { EXTERNAL_TAB_KEYS } from '../utils/multipleTabsChat';

const MAXIMUM_TRY_COUNT = 10;

export const onSocketClosed = (
    closeEvent,
    initializeChat,
    setStartTimer,
    pingTimer,
    setWebsocket,
    genesysConvID,
    token,
    conversationId,
    webSocketTryCount,
    setWebSocketTryCount,
    setIsChatInputDisabled,
    setShowReconnectButton,
    setIsLoading,
    setErrorState,
    showChatButton,
    setIsChatReady,
    setShowTimer,
    sendSurveyLink,
    isCSPEnabled,
    otherDummyTabAlreadyHandled,
    handleSendStatusToExternalTab,
) => {
    if (closeEvent.code === 4107) { // Other dummyTab already opened a socket
        otherDummyTabAlreadyHandled();
        return;
    }
    if (
        !showChatButton && webSocketTryCount.current < MAXIMUM_TRY_COUNT
        && (
            closeEvent.code === 1006 // 1006 means abnormal closure of websocket
            || closeEvent.code === 4999 // 4999 means client (us) did not recieve last pong
            || closeEvent.code === 4004 // There was an unexpected Genesys Socket Failure
            || closeEvent.reason === '789cb3ca-1794-4dda-9f48-262827e2b7c2' // Cannot force 1006 closer, see test case
            || closeEvent.code === 4102 // UnAuthorized Ticket
        )

    ) {
        setTimeout(() => {
            setWebSocketTryCount(webSocketTryCount.current + 1);
            initializeChat(); // initializes chat if socket disconnects before the conversation ended
        }, 100);
    } else if (
        closeEvent.code === 1000  // User closed the socket
        || closeEvent.code === 4002 // Agent manually closed conversation on Genesys
        || closeEvent.code === 4003 // Aigo success close the conversation
        || closeEvent.code === 4005 // Aigo success close the conversation
    ) {
        if (closeEvent.code === 4002) {
            sendSurveyLink();
        }
        endConversation(token, conversationId, isCSPEnabled);  // Tell Garden API conversation is over
        setIsChatReady(false);
        setIsChatInputDisabled(true); // Disable the chat input when the conversation is closed.
        setShowReconnectButton(true); // Enable reconnect button when the conversation is closed.
        handleSendStatusToExternalTab(EXTERNAL_TAB_KEYS.ON_AGENT_CLOSE);
        if (genesysConvID) {
            storeAttributionDataFromChatWidget({
                convId: genesysConvID,
                chatEnded: true,
                endDate: new Date(),
                isCurrentlyChatting: false,
            });
        }
    } else if (!showChatButton) {
        endConversation(token, conversationId, isCSPEnabled);  // Tell Garden API conversation is over
        setIsChatInputDisabled(true); // Disable the chat input when the conversation is closed.
        setShowReconnectButton(true); // Enable reconnect button when the conversation is closed.
        setIsLoading(false);
        setErrorState({ error: true, errorMessage: 'Failed to connect. Please try again.' });
        if (genesysConvID) {
            storeAttributionDataFromChatWidget({
                convId: genesysConvID,
                chatEnded: true,
                endDate: new Date(),
                isCurrentlyChatting: false,
            });
        }
    }
    setStartTimer(false);
    setShowTimer(false);
    clearInterval(pingTimer.current);
    setWebsocket({ type: 'reset' });
};

/**
 * Called when the websocket successfully established with the server.
 */
export const onSocketOpen = (setStartTimer, setIsChatInputDisabled, setShowReconnectButton) => {
    setStartTimer(true);
    setShowReconnectButton(false); // Disable reconnect button when the conversation is open.
};

/**
 * Check if the feature flag is enabled for the given use case
 * @param {*} name Use case name.
 * @param {*} featureFlags Feature flags object.
 * @returns {*} `true` if the use case exists and its feature flag is enabled, otherwise `false`.
 */
export const isUseCaseEnabled = (name, featureFlags) => USE_CASES.some(
    (useCase) => useCase?.name === name && featureFlags?.[useCase?.featureFlag],
);

/**
 * Initial message sent to the Aigo websocket with customer information
 */
export const createInitChatMessage = (
    ConvID,
    customerData,
    brand,
    bannerCodesAttempted,
    featureFlags,
    isUseCaseSent,
    setIsUseCaseSent,
) => {
    const name = `${customerData?.profile?.firstName || ''} ${customerData?.profile?.lastName || ''}`;
    const timestamp = `${new Date().getTime()} ${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
    const { bannerCodes, bannerCodesAttemptCount } = parseBannerCodes(bannerCodesAttempted);
    const payload = {
        timestamp,
        name: (name?.trim() && name) || '',
        email: customerData?.profile?.email || '',
    };
    if (bannerCodes) payload.bannerCodes = bannerCodes;
    if (
        !isUseCaseSent
        && isUseCaseEnabled(SUGGEST_PROMO_CODE_USE_CASE, featureFlags)
        && bannerCodesAttemptCount === MAX_INVALID_PROMO_CODES_ATTEMPTS
    ) {
        payload.useCase = SUGGEST_PROMO_CODE_USE_CASE;
        setIsUseCaseSent(true);
    }

    return JSON.stringify({
        initialChatMessage: true,
        ConvID,
        parameters: payload,
        Brand: brand,
        channel: 'web',
    });
};

export const createCSPInitChatMessage = (
    ConvID,
    customerData,
    brand,
    bannerCodesAttempted,
    featureFlags,
    isUseCaseSent,
    setIsUseCaseSent,
) => {
    const name = `${customerData?.profile?.firstName || ''} ${customerData?.profile?.lastName || ''}`;
    const timestamp = `${new Date().getTime()} ${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
    const { bannerCodes, bannerCodesAttemptCount } = parseBannerCodes(bannerCodesAttempted);
    const payload = {
        timestamp,
        name: (name?.trim() && name) || '',
        email: customerData?.profile?.email || '',
    };
    if (bannerCodes) payload.bannerCodes = bannerCodes;
    if (
        !isUseCaseSent
        && isUseCaseEnabled(SUGGEST_PROMO_CODE_USE_CASE, featureFlags)
        && bannerCodesAttemptCount === MAX_INVALID_PROMO_CODES_ATTEMPTS
    ) {
        payload.useCase = SUGGEST_PROMO_CODE_USE_CASE;
        setIsUseCaseSent(true);
    }

    console.log(JSON.stringify({
        initialChatMessage: true,
        ConvID,
        parameters: payload,
        Brand: brand,
        channel: 'web',
    }));

    return JSON.stringify({
        initialChatMessage: true,
        conversation_id: ConvID,
        parameters: payload,
        Brand: brand,
        channel: 'web',
    });
};

/**
 * Called when the websocket receives a message event
 * @param {*} evt The message event
 */
export const onSocketMessage = ({
    evt,
    userName,
    isAgentTypingMessage,
    setAgentIsTyping,
    setConversationId,
    conversationId,
    customerData,
    setInitialMessageSent,
    sendMessageToWebSocket,
    setGenesysConvID,
    setIsAigoReady,
    setIsChatReady,
    setStartTimer,
    setIsLoading,
    handleStateChangeForStatusOnWebsocketMessage,
    setMessages,
    setBotIsTyping,
    handleCreateSurveyLink,
    handleChatEventData,
    bannerCodesAttempted,
    setIsQualifiedChat,
    isChatWidgetMinimized,
    isUserOnDifferentTab,
    isSoundNotificationOn,
    soundNotification,
    setTempManualClosedCode,
    handleSendMessageToExternalTab,
    handleSendStatusToExternalTab,
    setConnectedToAigoCheck,
    setIsChatInputDisabled,
    isAgent,
}) => {
    try {
        const responseMessage = JSON.parse(evt.data);
        const messageResponse = {
            userName, responseMessage, msgType: 'SERVER', disable: false,
        };
        // This is for temporary solution to agent survey link. We will change it when we find a permanent solution.
        if (responseMessage?.status === 'genesys-conversation-close' && responseMessage?.source === 'agent') setTempManualClosedCode(4002);

        if (responseMessage?.status === 'connected-to-aigo') {
            setConnectedToAigoCheck(true);
        }

        if (isSoundNotificationOn && (isUserOnDifferentTab || isChatWidgetMinimized) && (responseMessage?.o || responseMessage?.contents?.message)) {
            playAudio(soundNotification);
        }

        if (isAgentTypingMessage(responseMessage)) {
            setAgentIsTyping(true);
        }

        if (responseMessage?.status === 'typing-indicator' && responseMessage?.source === 'bot') {
            setBotIsTyping(true);
            setTimeout(() => { setBotIsTyping(false); }, 3000);
        }

        if (responseMessage?.parameters?.isQualifiedSession === 'True') {
            setIsQualifiedChat(true);
        }

        // Set the conversationId on first message received
        if (responseMessage.initialConvID) {
            setConversationId(responseMessage.initialConvID);
            if (customerData?.auth?.isAuthenticated) {
                setInitialMessageSent(true);
                sendMessageToWebSocket(createInitChatMessage(responseMessage.initialConvID, customerData, '', bannerCodesAttempted));
            }
        }

        if (responseMessage.genesysConvID) {
            const genesysConvId = responseMessage.genesysConvID;
            setGenesysConvID(genesysConvId);
            storeAttributionDataFromChatWidget({
                convId: genesysConvId,
                chatEnded: false,
                endDate: null,
                isCurrentlyChatting: true,
            });
        }
        if (messageResponse.msgType === 'SERVER' && responseMessage?.o?.MsgValue === 0) {
            setIsChatReady(true);
            setStartTimer(true);
            setIsLoading(false);
            setIsAigoReady(true);
        }

        if (responseMessage?.status) {
            handleStateChangeForStatusOnWebsocketMessage(responseMessage?.status);
        }
        // handle csp status
        if (responseMessage?.header?.statusCode) {
            const { statusCode = '' } = responseMessage?.header || {};
            handleStateChangeForStatusOnWebsocketMessage(statusCode, true);
            if (statusCode === 'genesys-conversation-close') {
                handleSendStatusToExternalTab(statusCode);
            }
        }
        if (responseMessage?.status === 'agent-join') {
            handleCreateSurveyLink(responseMessage);
            handleChatEventData(responseMessage);
        }
        // If status exists, its for UI changes / system updates / system communication.
        // Not for display.
        if (!responseMessage?.status) {
            if (messageResponse.responseMessage.source === 'agent') {
                setMessages((oldMessages) => [...oldMessages, messageResponse]);
                handleSendMessageToExternalTab(messageResponse);
            } else if (!messageResponse?.responseMessage?.ConvID || conversationId === messageResponse.responseMessage.ConvID) {
                setMessages((oldMessages) => [...oldMessages, messageResponse]);
                handleSendMessageToExternalTab(messageResponse);
            }
            if (!(responseMessage?.messageType === 'html' && responseMessage?.o.includes('understand and agree'))) {
                setIsChatInputDisabled(false);
            }
        }
        // if connected to genesys agent then dont ever disbale chat input
        if (isAgent.current) {
            setIsChatInputDisabled(false);
        }
    } catch (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            errorMessage: error,
            messageEvent: evt,
            message: `AigoWebchatv2: Error on received messages. convid: ${conversationId} - ERROR: ${error}`,
            conversationId,
        });
    }
};

/**
 * websocket error handler
 */
export const onSocketError = async (error, webSocketTryCount, setErrorState, conversationId) => {
    // If this is the MAXIMUM_TRY_COUNT attempt at getting a socket, fail and set error state
    if (webSocketTryCount.current >= MAXIMUM_TRY_COUNT) {
        setErrorState({ error: true, errorMessage: 'Failed to connect. Please exit chat and try again.' });
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            errorMessage: error,
            message: `AigoWebchatv2: Socket Error. Failure - convid: ${conversationId} - ERROR: ${error}`,
            conversationId,
        });
    }
};
